import axiosInstance from "./axiosConfig";
import { handleAPIError } from "./utils/errors";
import type {
  GenerateIntakeTitleRequest,
  IntakeTitleContextMessage,
  GenerateIntakeTitleResponse,
  OutgoingIntake,
  OutgoingIntakeFormNode,
  OutgoingIntakeLintResult,
  OutgoingIntakeSubmission,
  OutgoingIntakeTemplate,
  OutgoingIntakeSavedTemplate,
  OutgoingIntakeReviewDecision,
  OutgoingIntakeReminderSchedule,
  OutgoingIntakeSaveTarget,
  OutgoingIntakeSubmissionView,
  OutgoingIntakeTargetSuggestions,
  OutgoingIntakeTemplateDefaults,
  SendSavedTemplateRequest,
  SendSavedTemplateResponse,
  WelcomeVideoUploadMeta
} from "./types/outgoingIntake";

const BASE_FOREIGN_NATIONALS_URL = "/foreign_nationals";

/** Who's speaking, for Casey's talking points; `due` is spoken ("October 29"). */
export type TalkingPointsRequest = {
  attorney_name?: string | null;
  firm_name?: string | null;
  due?: string | null;
};

/** The request email as the client would get it if the intake were sent now. */
export type IntakeEmailPreview = {
  subject: string;
  html: string;
  from_name: string;
  reply_to: string | null;
};

/** A welcome-video upload: the file plus its length, trim and library choice. */
export const welcomeVideoForm = (
  file: File | Blob,
  filename: string,
  meta: WelcomeVideoUploadMeta & { scope?: string; title?: string | null } = {}
): FormData => {
  const form = new FormData();
  form.append("file", file, filename);
  if (meta.durationSeconds)
    form.append("duration_seconds", String(meta.durationSeconds));
  if (meta.trim?.start)
    form.append("trim_start_seconds", String(meta.trim.start));
  if (meta.trim?.end) form.append("trim_end_seconds", String(meta.trim.end));
  if (meta.saveToMyVideos) form.append("save_to_my_videos", "1");
  if (meta.scope) form.append("scope", meta.scope);
  if (meta.title) form.append("title", meta.title);
  return form;
};

export type OutgoingIntakeFileResponse = {
  filePath: string;
  data: string; // base64
};

export const OutgoingIntakesAPI = {
  /**
   * Catalog metadata for every template. Schemas are omitted unless their
   * template is named in `templateIds` — the whole catalog's schemas run to
   * megabytes, so the builder pulls them as templates are actually used.
   */
  async listTemplates(options?: {
    templateIds?: string[];
  }): Promise<OutgoingIntakeTemplate[]> {
    try {
      const templateIds = (options?.templateIds ?? [])
        .map((id) => String(id ?? "").trim())
        .filter(Boolean);
      const response = await axiosInstance.get<{
        templates: OutgoingIntakeTemplate[];
      }>(`/outgoing_intakes/templates`, {
        params: templateIds.length
          ? { template_ids: templateIds.join(",") }
          : undefined
      });
      return Array.isArray(response.data?.templates)
        ? response.data.templates
        : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** The firm's templates; archived ones only with `includeArchived`. */
  async listSavedTemplates(options?: {
    includeArchived?: boolean;
  }): Promise<OutgoingIntakeSavedTemplate[]> {
    try {
      const response = await axiosInstance.get<{
        templates: OutgoingIntakeSavedTemplate[];
      }>(`/outgoing_intakes/saved_templates`, {
        params: options?.includeArchived
          ? { include_archived: 1, limit: 200 }
          : undefined
      });
      return Array.isArray(response.data?.templates)
        ? response.data.templates
        : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createSavedTemplate(
    payload: {
      name: string;
      schema: OutgoingIntakeFormNode[];
      source_outgoing_intake_id?: string | null;
      visa?: string | null;
      description?: string | null;
    } & OutgoingIntakeTemplateDefaults
  ): Promise<OutgoingIntakeSavedTemplate> {
    try {
      const response = await axiosInstance.post<OutgoingIntakeSavedTemplate>(
        `/outgoing_intakes/saved_templates`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** `visa` / `description` / a default null clears it; `archived` archives or restores. */
  async updateSavedTemplate(
    templateId: string,
    payload: {
      name?: string;
      schema?: OutgoingIntakeFormNode[];
      visa?: string | null;
      description?: string | null;
      archived?: boolean;
    } & OutgoingIntakeTemplateDefaults
  ): Promise<OutgoingIntakeSavedTemplate> {
    try {
      const response = await axiosInstance.patch<OutgoingIntakeSavedTemplate>(
        `/outgoing_intakes/saved_templates/${templateId}`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * Sends a template (one of the firm's, or a catalog id such as a visa
   * starter) to a client: the intake is created in their Requests and sent,
   * or only created with `send: false`.
   */
  async sendSavedTemplate(
    templateId: string,
    payload: SendSavedTemplateRequest
  ): Promise<SendSavedTemplateResponse> {
    try {
      const response = await axiosInstance.post<SendSavedTemplateResponse>(
        `/outgoing_intakes/saved_templates/${encodeURIComponent(
          templateId
        )}/send`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Upload a template's welcome video into the firm's shared videos. */
  async uploadSavedTemplateWelcomeVideo(
    templateId: string,
    file: File | Blob,
    options: WelcomeVideoUploadMeta & {
      filename?: string;
      onProgress?: (fraction: number) => void;
      signal?: AbortSignal;
    } = {}
  ): Promise<OutgoingIntakeSavedTemplate> {
    try {
      const formData = welcomeVideoForm(
        file,
        options.filename ?? "welcome-video.webm",
        options
      );
      const response = await axiosInstance.post<OutgoingIntakeSavedTemplate>(
        `/outgoing_intakes/saved_templates/${templateId}/welcome_video`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: options.onProgress
            ? (e) => options.onProgress?.(e.total ? e.loaded / e.total : 0)
            : undefined,
          signal: options.signal
        }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Give a template a video already in the firm's library. */
  async setSavedTemplateLibraryVideo(
    templateId: string,
    videoId: string
  ): Promise<OutgoingIntakeSavedTemplate> {
    try {
      const response = await axiosInstance.post<OutgoingIntakeSavedTemplate>(
        `/outgoing_intakes/saved_templates/${templateId}/welcome_video`,
        { video_id: videoId }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Take the video off a template; it stays in the firm's library. */
  async deleteSavedTemplateWelcomeVideo(
    templateId: string
  ): Promise<OutgoingIntakeSavedTemplate> {
    try {
      const response = await axiosInstance.delete<OutgoingIntakeSavedTemplate>(
        `/outgoing_intakes/saved_templates/${templateId}/welcome_video`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * One of the firm's templates from an intake: its questions plus its note,
   * video, expiry, reminders and branding as the template's defaults.
   */
  async saveIntakeAsTemplate(
    foreignNationalId: string,
    outgoingIntakeId: string,
    body: { name?: string } = {}
  ): Promise<{
    template: OutgoingIntakeSavedTemplate;
    already_saved: boolean;
  }> {
    try {
      const response = await axiosInstance.post<{
        template: OutgoingIntakeSavedTemplate;
        already_saved: boolean;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/save_as_template`,
        body
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async deleteSavedTemplate(templateId: string): Promise<void> {
    try {
      await axiosInstance.delete(
        `/outgoing_intakes/saved_templates/${templateId}`
      );
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** The profile fields an answer can be written to, for the "Saves to" picker. */
  async listSaveTargets(): Promise<OutgoingIntakeSaveTarget[]> {
    try {
      const response = await axiosInstance.get<{
        targets: OutgoingIntakeSaveTarget[];
      }>(`/outgoing_intakes/save_targets`);
      return Array.isArray(response.data?.targets) ? response.data.targets : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listForForeignNational(
    foreignNationalId: string
  ): Promise<OutgoingIntake[]> {
    try {
      const response = await axiosInstance.get<{
        outgoing_intakes: OutgoingIntake[];
      }>(`${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes`);
      return Array.isArray(response.data?.outgoing_intakes)
        ? response.data.outgoing_intakes
        : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createForForeignNational(
    foreignNationalId: string,
    payload: { template_ids: string[]; title?: string }
  ): Promise<OutgoingIntake> {
    try {
      const response = await axiosInstance.post<OutgoingIntake>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async generateTitle(
    foreignNationalId: string,
    schema: OutgoingIntakeFormNode[],
    messages?: IntakeTitleContextMessage[]
  ): Promise<string> {
    try {
      const payload: GenerateIntakeTitleRequest = {
        schema,
        ...(messages && messages.length > 0 ? { messages } : {})
      };
      const response = await axiosInstance.post<GenerateIntakeTitleResponse>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/generate_title`,
        payload
      );
      return response.data?.title ?? "";
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async get(
    foreignNationalId: string,
    outgoingIntakeId: string
  ): Promise<{
    outgoing_intake: OutgoingIntake;
    submissions: OutgoingIntakeSubmission[];
  }> {
    try {
      const response = await axiosInstance.get<{
        outgoing_intake: OutgoingIntake;
        submissions: OutgoingIntakeSubmission[];
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}`
      );
      return {
        outgoing_intake: response.data?.outgoing_intake,
        submissions: Array.isArray(response.data?.submissions)
          ? response.data.submissions
          : []
      };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async patch(
    foreignNationalId: string,
    outgoingIntakeId: string,
    payload: {
      title?: string;
      form_schema?: unknown[];
      enable_firm_branding?: boolean;
      intake_background_color?: string | null;
      recipient_email?: string;
      attorney_note?: string;
      expires_in_days?: number;
      reminder_schedule?: OutgoingIntakeReminderSchedule | null;
    }
  ): Promise<OutgoingIntake> {
    try {
      const response = await axiosInstance.patch<OutgoingIntake>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async delete(
    foreignNationalId: string,
    outgoingIntakeId: string
  ): Promise<{ success: true }> {
    try {
      const response = await axiosInstance.delete<{ success: true }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async send(
    foreignNationalId: string,
    outgoingIntakeId: string,
    payload: {
      recipient_email?: string;
      expires_in_days?: number;
      attorney_note?: string;
      save_as_template?: boolean;
      template_name?: string;
      reminder_schedule?: OutgoingIntakeReminderSchedule | null;
    }
  ): Promise<{
    outgoing_intake: OutgoingIntake;
    public_link: string;
    email_delivered?: boolean;
    saved_template_id?: string;
  }> {
    try {
      const response = await axiosInstance.post<{
        outgoing_intake: OutgoingIntake;
        public_link: string;
        email_delivered?: boolean;
        saved_template_id?: string;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/send`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * Renders the request email without sending it. `message` and `title` are
   * the draft's unsaved note and title; omitted ones fall back to the saved draft.
   */
  async emailPreview(
    foreignNationalId: string,
    outgoingIntakeId: string,
    payload: { message?: string; title?: string } = {}
  ): Promise<IntakeEmailPreview> {
    try {
      const response = await axiosInstance.post<IntakeEmailPreview>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/email_preview`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * Nudges a client about an intake they already have. Recoverable links are
   * re-sent as-is; legacy hash-only links are replaced.
   */
  async remind(
    foreignNationalId: string,
    outgoingIntakeId: string,
    payload: { expires_in_days?: number; message?: string } = {}
  ): Promise<{
    outgoing_intake: OutgoingIntake;
    public_link: string;
    reissued_link?: boolean;
    email_delivered?: boolean;
  }> {
    try {
      const response = await axiosInstance.post<{
        outgoing_intake: OutgoingIntake;
        public_link: string;
        reissued_link?: boolean;
        email_delivered?: boolean;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/remind`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Push the link's expiry out by `days` (an expired intake comes back to the client). */
  async extend(
    foreignNationalId: string,
    outgoingIntakeId: string,
    days: number
  ): Promise<OutgoingIntake> {
    try {
      const response = await axiosInstance.post<{
        outgoing_intake: OutgoingIntake;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/extend`,
        { days }
      );
      return response.data.outgoing_intake;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Stop the client's link working; the intake itself is unchanged. */
  async revoke(
    foreignNationalId: string,
    outgoingIntakeId: string
  ): Promise<OutgoingIntake> {
    try {
      const response = await axiosInstance.post<{
        outgoing_intake: OutgoingIntake;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/revoke`,
        {}
      );
      return response.data.outgoing_intake;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Revoke the link and close the intake as expired. */
  async cancel(
    foreignNationalId: string,
    outgoingIntakeId: string
  ): Promise<OutgoingIntake> {
    try {
      const response = await axiosInstance.post<{
        outgoing_intake: OutgoingIntake;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/cancel`,
        {}
      );
      return response.data.outgoing_intake;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * Fetches the client's current intake link. Links aren't included in intake
   * payloads, so anything that needs one asks for it at the moment of use.
   */
  /** Before sending: what the client would read that sounds internal. */
  async lint(
    foreignNationalId: string,
    outgoingIntakeId: string,
    body: { schema?: OutgoingIntakeFormNode[]; attorney_note?: string | null }
  ): Promise<OutgoingIntakeLintResult> {
    try {
      const response = await axiosInstance.post<OutgoingIntakeLintResult>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/lint`,
        body
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getLink(
    foreignNationalId: string,
    outgoingIntakeId: string
  ): Promise<{ public_link: string; expires_at?: unknown }> {
    try {
      const response = await axiosInstance.get<{
        public_link: string;
        expires_at?: unknown;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/link`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async uploadWelcomeVideo(
    foreignNationalId: string,
    outgoingIntakeId: string,
    file: File | Blob,
    filename = "welcome-video.webm",
    /** Share of the file sent so far, 0 to 1. */
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal,
    meta: WelcomeVideoUploadMeta = {}
  ): Promise<OutgoingIntake> {
    try {
      const formData = welcomeVideoForm(file, filename, meta);
      const response = await axiosInstance.post<{
        outgoing_intake: OutgoingIntake;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/welcome_video`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: onProgress
            ? (e) => onProgress(e.total ? e.loaded / e.total : 0)
            : undefined,
          signal
        }
      );
      return response.data?.outgoing_intake;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Use a Previous Video: one of the attorney's or the firm's. */
  async attachLibraryWelcomeVideo(
    foreignNationalId: string,
    outgoingIntakeId: string,
    videoId: string
  ): Promise<OutgoingIntake> {
    try {
      const response = await axiosInstance.post<{
        outgoing_intake: OutgoingIntake;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/welcome_video/library`,
        { video_id: videoId }
      );
      return response.data?.outgoing_intake;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Casey's talking points for this intake's video; empty when it couldn't. */
  async draftTalkingPoints(
    foreignNationalId: string,
    outgoingIntakeId: string,
    body: TalkingPointsRequest
  ): Promise<string[]> {
    try {
      const response = await axiosInstance.post<{ lines: string[] }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/welcome_video/talking_points`,
        body
      );
      return Array.isArray(response.data?.lines) ? response.data.lines : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Casey's talking points for a template's video: no name, no dates. */
  async draftTemplateTalkingPoints(
    templateId: string,
    body: Omit<TalkingPointsRequest, "due">
  ): Promise<string[]> {
    try {
      const response = await axiosInstance.post<{ lines: string[] }>(
        `/outgoing_intakes/saved_templates/${templateId}/welcome_video/talking_points`,
        body
      );
      return Array.isArray(response.data?.lines) ? response.data.lines : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async deleteWelcomeVideo(
    foreignNationalId: string,
    outgoingIntakeId: string
  ): Promise<OutgoingIntake> {
    try {
      const response = await axiosInstance.delete<{
        outgoing_intake: OutgoingIntake;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/welcome_video`
      );
      return response.data?.outgoing_intake;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async reviewSubmission(
    foreignNationalId: string,
    outgoingIntakeId: string,
    submissionId: string,
    payload: { decisions: OutgoingIntakeReviewDecision[] }
  ): Promise<OutgoingIntakeSubmission> {
    try {
      const response = await axiosInstance.post<{
        submission: OutgoingIntakeSubmission;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/submissions/${submissionId}/review`,
        payload
      );
      return response.data?.submission;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async requestRevision(
    foreignNationalId: string,
    outgoingIntakeId: string,
    submissionId: string,
    payload: {
      decisions: OutgoingIntakeReviewDecision[];
      revision_request_note?: string | null;
    }
  ): Promise<{
    submission: OutgoingIntakeSubmission;
    email_delivered?: boolean;
  }> {
    try {
      const response = await axiosInstance.post<{
        submission: OutgoingIntakeSubmission;
        email_delivered?: boolean;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/submissions/${submissionId}/request_revision`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async applySubmission(
    foreignNationalId: string,
    outgoingIntakeId: string,
    submissionId: string,
    payload: {
      accept_all: boolean;
      decisions: OutgoingIntakeReviewDecision[];
      notify_client?: boolean;
    }
  ): Promise<{
    submission: OutgoingIntakeSubmission | null;
    email_delivered?: boolean;
  }> {
    try {
      const response = await axiosInstance.post<{
        submission: OutgoingIntakeSubmission | null;
        email_delivered?: boolean;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/submissions/${submissionId}/apply`,
        payload
      );
      return {
        submission: response.data?.submission ?? null,
        email_delivered: response.data?.email_delivered
      };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Each answer beside what the client's record holds today. */
  async getSubmissionView(
    foreignNationalId: string,
    outgoingIntakeId: string,
    submissionId: string
  ): Promise<OutgoingIntakeSubmissionView> {
    try {
      const response = await axiosInstance.get<OutgoingIntakeSubmissionView>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/submissions/${submissionId}/view`
      );
      return {
        ...response.data,
        answers: Array.isArray(response.data?.answers)
          ? response.data.answers
          : []
      };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** A profile field for each answer that isn't saved anywhere (the review's suggestion chips). */
  async suggestTargets(
    foreignNationalId: string,
    outgoingIntakeId: string,
    submissionId: string,
    body: { answer_keys?: string[] } = {}
  ): Promise<OutgoingIntakeTargetSuggestions> {
    try {
      const response =
        await axiosInstance.post<OutgoingIntakeTargetSuggestions>(
          `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/submissions/${submissionId}/suggest_targets`,
          body
        );
      return {
        suggestions: Array.isArray(response.data?.suggestions)
          ? response.data.suggestions
          : [],
        model_checked: Boolean(response.data?.model_checked)
      };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Send a one-question follow-up intake asking for a fresh copy of one answer. */
  async requestNewCopy(
    foreignNationalId: string,
    outgoingIntakeId: string,
    submissionId: string,
    payload: { answer_key: string; note: string }
  ): Promise<{ outgoing_intake: OutgoingIntake; email_delivered?: boolean }> {
    try {
      const response = await axiosInstance.post<{
        outgoing_intake: OutgoingIntake;
        email_delivered?: boolean;
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/submissions/${submissionId}/request_new_copy`,
        payload
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Writes accepted, mapped answers to the profile; `answer_keys` limits it to a subset. */
  async applyAcceptedFields(
    foreignNationalId: string,
    outgoingIntakeId: string,
    submissionId: string,
    payload: { answer_keys?: string[] } = {}
  ): Promise<{ applied_count: number; changed_fields?: string[] }> {
    try {
      const response = await axiosInstance.post<{
        applied_count: number;
        changed_fields?: string[];
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/submissions/${submissionId}/apply_accepted_fields`,
        payload
      );
      return {
        applied_count: response.data?.applied_count ?? 0,
        changed_fields: response.data?.changed_fields ?? []
      };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async saveAcceptedDocuments(
    foreignNationalId: string,
    outgoingIntakeId: string,
    submissionId: string,
    payload: { answer_keys?: string[]; file_ids?: string[] } = {}
  ): Promise<{ saved_count: number; document_ids: string[] }> {
    try {
      const response = await axiosInstance.post<{
        saved_count: number;
        document_ids: string[];
      }>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/submissions/${submissionId}/save_accepted_documents`,
        payload
      );
      return {
        saved_count: response.data?.saved_count ?? 0,
        document_ids: response.data?.document_ids ?? []
      };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getFile(
    foreignNationalId: string,
    outgoingIntakeId: string,
    fileId: string
  ): Promise<OutgoingIntakeFileResponse> {
    try {
      const response = await axiosInstance.get<OutgoingIntakeFileResponse>(
        `${BASE_FOREIGN_NATIONALS_URL}/${foreignNationalId}/outgoing_intakes/${outgoingIntakeId}/files/${fileId}/file`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  }
};
